<template>
  <div class="w-full px-3 py-2 flex" :class="classes">
    <div
      v-if="copyright?.file?.type"
      class="min-w-[40px] max-w-[40px] min-h-[40px] max-h-[40px] mr-3 p-0.5 rounded-sm bg-gray-200/20 relative"
    >
      <ThumbnailIcon
        class="w-full h-full"
        :image-url="applyAuthorizationToken(notification.primaryIcon.imageUrl)"
        :icon-size="36"
        :type="copyright.file.type"
      />
    </div>

    <Avatar
      v-else
      class="mr-3"
      :size="36"
      :src="notification.primaryIcon.imageUrl"
    />

    <div
      class="font-secondary text-[14px] leading-5 tablet-h:leading-[19px] tablet-h:text-[13px] text-gray-800 -mt-1 flex flex-col justify-between"
    >
      <component :is="copy" class="mb-1 tablet-h:mb-0.5" />
      <div class="flex items-center justify-between">
        <div class="text-[13px] tablet-h:text-[12px] opacity-50">
          {{ formatDistanceToNowStrict(props.notification.timestamp) }} ago
        </div>

        <Pill v-if="showStatus" :success="isAccepted" :danger="isRemoved">
          {{ status }}
        </Pill>
      </div>
      <div v-if="showActionButtons" class="w-full flex gap-x-2.5 mt-2">
        <UiButton
          class="w-full!"
          :loading="loadingAccept"
          :disabled="loadingDecline"
          primary
          @click="handleAcceptClick"
        >
          Accept
        </UiButton>
        <UiButton
          class="w-full!"
          :loading="loadingDecline"
          :disabled="loadingAccept"
          @click="handleDeclineClick"
        >
          Decline
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  InviteStatus,
  NotificationType,
  useAcceptInviteMutation,
  useDeclineInviteMutation,
  type Copyright,
  type InviteMetadata,
  type Notification,
} from '@/shared/graphql/legacy/generated'
import { apolloClient } from '@/shared/graphql/client'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { Pill, ThumbnailIcon, UiButton, Avatar } from '@dacr/ui'
import { formatDistanceToNowStrict } from 'date-fns'
import { computed, h } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps<{
  notification: Notification
}>()

const { loading: loadingAccept, mutate: acceptInvite } =
  useAcceptInviteMutation()

const { loading: loadingDecline, mutate: declineInvite } =
  useDeclineInviteMutation()

const classes = computed(() => ({
  'tablet-h:hover:bg-[#FAFAFA]': props.notification.read,
  'bg-green-900/10': !props.notification.read,
}))
const metadata = computed(() => props.notification.metadata as InviteMetadata)

const copyright = computed(() => metadata.value.invite.reference as Copyright)

const showStatus = computed(
  () =>
    !!(isRequest.value && metadata.value.invite.status !== InviteStatus.Pending)
)

const isAccepted = computed(
  () => metadata.value.invite.status === InviteStatus.Accepted
)

const isRemoved = computed(
  () => metadata.value.invite.status === InviteStatus.Removed
)

const status = computed(() => {
  if (isRemoved.value) {
    return 'Request removed'
  }

  return `${metadata.value.invite.status.charAt(0)}${metadata.value.invite.status.slice(1).toLowerCase()}`
})

const isRequest = computed(
  () =>
    props.notification.type === NotificationType.TeamMemberAccessPending ||
    props.notification.type === NotificationType.FileOwnerAccessPending ||
    props.notification.type === NotificationType.FileAuthorAccessPending
)

const showActionButtons = computed(
  () => isRequest.value && metadata.value.invite.status === InviteStatus.Pending
)

const copy = computed(() => {
  if (props.notification.type === NotificationType.FileOwnerAccessAccepted) {
    return h('div', [
      `${metadata.value.invite?.to?.name} has accepted your request to be listed as an owner for the copyright of `,
      h(
        RouterLink,
        {
          to: `/file/${copyright.value?.id}`,
          class: 'font-medium text-green-900 break-all',
        },
        () => copyright.value.file?.fileName
      ),
      '.',
    ])
  }

  if (props.notification.type === NotificationType.FileAuthorAccessAccepted) {
    return h('div', [
      `${metadata.value.invite?.to?.name} has accepted your request to be listed as an author for the copyright of `,
      h(
        RouterLink,
        {
          to: `/file/${copyright.value?.id}`,
          class: 'font-medium text-green-900 break-all',
        },
        () => copyright.value.file?.fileName
      ),
      '.',
    ])
  }

  if (props.notification.type === NotificationType.FileOwnerAccessPending) {
    return h('div', [
      `${metadata.value.invite?.from?.name} has requested to list you as one of the owners for the copright of `,
      h(
        RouterLink,
        {
          to: `/file/${copyright.value?.id}`,
          class: 'font-medium text-green-900 break-all',
        },
        () => copyright.value.file?.fileName
      ),
      ' Accept the request to be listed as an owner.',
    ])
  }

  if (props.notification.type === NotificationType.FileAuthorAccessPending) {
    return h('div', [
      `${metadata.value.invite?.from?.name} has requested to list you as one of the authors for the copright of `,
      h(
        RouterLink,
        {
          to: `/file/${copyright.value?.id}`,
          class: 'font-medium text-green-900 break-all',
        },
        () => copyright.value.file?.fileName
      ),
      ' Accept the request to be listed as an author.',
    ])
  }

  if (props.notification.type === NotificationType.FilePrivacyAccessAccepted) {
    return h('div', [
      `${metadata.value.invite?.from?.name} has invited you to view `,
      h(
        RouterLink,
        {
          to: `/file/${copyright.value?.id}`,
          class: 'font-medium text-green-900 break-all',
        },
        () => copyright.value.file?.fileName
      ),
      `.`,
    ])
  }

  if (
    props.notification.type === NotificationType.TeamMemberAccessPending &&
    metadata.value.invite.reference.__typename === 'Origin'
  ) {
    return h('div', [
      `You have been invited to join the `,

      h(
        'span',
        { class: 'font-medium text-green-900' },
        metadata.value.invite.reference.name
      ),
      ` team. Accept the invite to join the team.`,
    ])
  }

  if (
    props.notification.type === NotificationType.TeamMemberAccessAccepted &&
    metadata.value.invite.reference.__typename === 'Origin'
  ) {
    return h('div', [
      `${metadata.value.invite.to.name} has accepted your invite to join the `,
      h(
        'span',
        { class: 'font-medium text-green-900' },
        metadata.value.invite.reference.name
      ),
      ` team.`,
    ])
  }

  return null
})

const updateNotificationStatus = (status: InviteStatus) => {
  apolloClient.cache.modify({
    id: `Invite:${(props.notification.metadata as InviteMetadata).invite.id}`,
    fields: {
      status() {
        return status
      },
    },
  })
}

const handleAcceptClick = async () => {
  try {
    const metadata = props.notification.metadata as InviteMetadata
    await acceptInvite({
      id: metadata.invite.id,
    })
    updateNotificationStatus(InviteStatus.Accepted)
  } catch (error) {
    console.error('Action failed', error)
  }
}

const handleDeclineClick = async () => {
  try {
    const metadata = props.notification.metadata as InviteMetadata
    await declineInvite({
      id: metadata.invite.id,
    })
    updateNotificationStatus(InviteStatus.Declined)
  } catch (error) {
    console.error('Action failed', error)
  }
}
</script>
